import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, Snowflake, FileCheck2, Activity } from "lucide-react";
import { scrollToId } from "./Header";

const HERO_IMG = "https://images.unsplash.com/photo-1602052577122-f73b9710adba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NxAzMzV8MHwxfHNlYXJjaHwxfHxiaW90ZWNoJTIwbGFib3JhdG9yeSUyMHJlc2VhcmNoJTIwc2NpZW50aXN0JTIwbWljcm9zY29wZSUyMG1vbGVjdWxlJTIwcmVzZWFyY2glMjBsYWJ8ZW58MHx8fHwxNzkxNzA3Njk5fDA&ixlib=rb-4.1.0&q=85";

const LINES = [
    { text: "Precision peptides.", cls: "text-slate-900" },
    { text: "Verified purity.", cls: "text-cyan-700" },
    { text: "Australian research supply.", cls: "text-slate-900" },
];

function MoleculeVisual() {
    return (
        <svg viewBox="0 0 400 400" className="w-full h-full spin-slow" fill="none" aria-hidden="true">
            <circle cx="200" cy="200" r="150" stroke="#CBD5E1" strokeWidth="1" strokeDasharray="3 6" />
            <circle cx="200" cy="200" r="95" stroke="#CBD5E1" strokeWidth="1" strokeDasharray="3 6" />
            <g stroke="#1E3A8A" strokeWidth="2" strokeLinecap="round">
                <line x1="200" y1="200" x2="200" y2="80" />
                <line x1="200" y1="200" x2="308" y2="248" />
                <line x1="200" y1="200" x2="92" y2="248" />
                <line x1="200" y1="80" x2="276" y2="120" />
                <line x1="308" y1="248" x2="276" y2="120" />
                <line x1="92" y1="248" x2="140" y2="320" />
                <line x1="308" y1="248" x2="260" y2="320" />
                <line x1="140" y1="320" x2="260" y2="320" />
            </g>
            <circle cx="200" cy="200" r="14" fill="#0891B2" />
            <circle cx="200" cy="80" r="10" fill="#1E3A8A" />
            <circle cx="308" cy="248" r="10" fill="#1E3A8A" />
            <circle cx="92" cy="248" r="10" fill="#1E3A8A" />
            <circle cx="276" cy="120" r="7" fill="#0891B2" />
            <circle cx="140" cy="320" r="7" fill="#0891B2" />
            <circle cx="260" cy="320" r="7" fill="#0F172A" />
        </svg>
    );
}

export default function Hero() {
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
    const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 800], [0, 140]);
    const molY = useTransform(scrollY, [0, 800], [0, -60]);

    const onMove = (e) => {
        const rect = ref.current.getBoundingClientRect();
        mx.set((e.clientX - rect.left) / rect.width - 0.5);
        my.set((e.clientY - rect.top) / rect.height - 0.5);
    };

    return (
        <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16">
            <motion.div style={{ y: bgY }} className="absolute inset-0 pointer-events-none" aria-hidden="true">
                <div className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full bg-cyan-100/50 blur-3xl" />
                <div className="absolute bottom-0 -left-40 w-[480px] h-[480px] rounded-full bg-blue-100/60 blur-3xl" />
                <div
                    className="absolute inset-0 opacity-[0.35]"
                    style={{
                        backgroundImage: "linear-gradient(#E2E8F0 1px, transparent 1px), linear-gradient(90deg, #E2E8F0 1px, transparent 1px)",
                        backgroundSize: "72px 72px",
                        maskImage: "radial-gradient(ellipse 80% 60% at 50% 40%, black 20%, transparent 75%)",
                        WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 40%, black 20%, transparent 75%)",
                    }}
                />
            </motion.div>

            <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15, duration: 0.7 }}
                        className="flex items-center gap-3 mb-8"
                    >
                        <span className="w-2 h-2 rounded-full bg-cyan-600 pulse-dot" />
                        <span className="font-jbm text-xs uppercase tracking-[0.25em] text-slate-500">
                            <span className="hidden sm:inline">Melbourne · Sydney · Perth — </span>Est. for laboratory science
                        </span>
                    </motion.div>

                    <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-[4.4rem]" data-testid="hero-headline">
                        {LINES.map((line, i) => (
                            <span key={line.text} className="block overflow-hidden pb-1">
                                <motion.span
                                    initial={{ y: "115%" }}
                                    animate={{ y: 0 }}
                                    transition={{ delay: 0.3 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                                    className={`block ${line.cls}`}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.8 }}
                        className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-slate-600"
                    >
                        AusResearch supplies reference-grade research peptides and analytical standards to verified
                        Australian laboratories, universities and R&D institutions. Every batch is independently
                        HPLC-verified and shipped under monitored cold chain.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.05, duration: 0.8 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-browse-catalog-button"
                            onClick={() => scrollToId("#catalog")}
                            className="group flex items-center gap-2.5 bg-slate-900 text-white font-medium px-7 py-3.5 rounded-full hover:bg-cyan-800 transition-colors duration-300"
                        >
                            Browse the catalog
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <button
                            data-testid="hero-quality-button"
                            onClick={() => scrollToId("#quality")}
                            className="font-medium px-7 py-3.5 rounded-full border border-slate-300 text-slate-800 hover:border-cyan-700 hover:text-cyan-800 transition-colors duration-300"
                        >
                            Our quality standard
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.25, duration: 0.9 }}
                        className="mt-14 grid grid-cols-3 max-w-md gap-6"
                    >
                        {[
                            { v: "≥99%", l: "HPLC purity" },
                            { v: "50+", l: "Reference compounds" },
                            { v: "-20°C", l: "Cold chain" },
                        ].map((s) => (
                            <div key={s.l}>
                                <div className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{s.v}</div>
                                <div className="font-jbm text-[10px] sm:text-xs uppercase tracking-widest text-slate-500 mt-1">{s.l}</div>
                            </div>
                        ))}
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    style={{ y: molY }}
                    className="lg:col-span-5 [perspective:1200px]"
                >
                    <motion.div
                        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
                        data-testid="hero-visual-card"
                        className="relative rounded-3xl border border-slate-200 bg-white/80 backdrop-blur-xl shadow-[0_40px_80px_-20px_rgba(30,58,138,0.25)] p-8 overflow-hidden"
                    >
                        <div className="absolute inset-0 opacity-[0.08]">
                            <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="relative aspect-square max-w-[380px] mx-auto">
                            <MoleculeVisual />
                        </div>

                        <div className="absolute top-6 left-6 flex items-center gap-2 bg-white/90 backdrop-blur rounded-full px-4 py-2 border border-slate-200 shadow-sm float-y" style={{ transform: "translateZ(50px)" }}>
                            <Activity className="w-4 h-4 text-cyan-700" />
                            <span className="font-jbm text-xs text-slate-700">HPLC 99.4%</span>
                        </div>
                        <div className="absolute bottom-8 right-6 flex items-center gap-2 bg-white/90 backdrop-blur rounded-full px-4 py-2 border border-slate-200 shadow-sm float-y" style={{ transform: "translateZ(40px)", animationDelay: "-2s" }}>
                            <Snowflake className="w-4 h-4 text-blue-800" />
                            <span className="font-jbm text-xs text-slate-700">-20°C verified</span>
                        </div>
                        <div className="absolute bottom-24 left-4 flex items-center gap-2 bg-white/90 backdrop-blur rounded-full px-4 py-2 border border-slate-200 shadow-sm float-y" style={{ transform: "translateZ(60px)", animationDelay: "-4s" }}>
                            <FileCheck2 className="w-4 h-4 text-slate-800" />
                            <span className="font-jbm text-xs text-slate-700">COA per batch</span>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
